import type { Cell, ResultMatrix } from "./model"
import { Colour } from "./model"

const COLOURS: Record<Colour, string> = {
  [Colour.Red]: "#ef4444",
  [Colour.Orange]: "#f97316",
  [Colour.Yellow]: "#eab308",
  [Colour.Green]: "#22c55e",
  [Colour.Cyan]: "#06b6d4",
  [Colour.Blue]: "#3b82f6",
  [Colour.Violet]: "#8b5cf6",
}

// Black text on the light bands, white on the dark ones
const TEXT_COLOURS: Record<Colour, string> = {
  [Colour.Red]: "#ffffff",
  [Colour.Orange]: "#000000",
  [Colour.Yellow]: "#000000",
  [Colour.Green]: "#ffffff",
  [Colour.Cyan]: "#000000",
  [Colour.Blue]: "#ffffff",
  [Colour.Violet]: "#000000",
}

type CellProps = {
  readonly cell: Cell
}

function MatrixCell({ cell }: CellProps) {
  return (
    <td
      className="size-9 border border-background text-center text-xs tabular-nums"
      style={{
        backgroundColor: COLOURS[cell.colour],
        color: TEXT_COLOURS[cell.colour],
      }}
    >
      {cell.value}
    </td>
  )
}

type Props = {
  readonly matrix: ResultMatrix
  /**
   * Optional phase → display label map; rows and columns stay unlabelled
   * when not provided
   */
  readonly phaseLabels?: Record<string, string>
}

export default function Matrix({ matrix, phaseLabels }: Props) {
  // Key order is the canonical phase order (pluto…venus), matching the
  // positional order of matrix.cells on both axes
  const colEntries = Object.entries(matrix.colSums)
  const rowEntries = Object.entries(matrix.rowSums)

  return (
    <table className="border-collapse text-sm">
      <thead>
        <tr>
          <td />
          {colEntries.map(([phase, sum]) => (
            <th key={phase} scope="col" className="px-1 py-1 font-medium">
              {phaseLabels && (
                <div className="text-xs font-normal text-muted-foreground">
                  {phaseLabels[phase]}
                </div>
              )}
              {sum}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rowEntries.map(([phase, sum], rowIndex) => (
          <tr key={phase}>
            <th
              scope="row"
              className="px-2 py-1 text-right font-medium whitespace-nowrap"
            >
              {phaseLabels && (
                <span className="mr-1 text-xs font-normal text-muted-foreground">
                  {phaseLabels[phase]}
                </span>
              )}
              {sum}
            </th>
            {(matrix.cells[rowIndex] ?? []).map((cell, colIndex) => (
              <MatrixCell
                key={colEntries[colIndex]?.[0] ?? colIndex}
                cell={cell}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
